import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appLogsApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { AlertTriangle, Info, AlertCircle, Trash2, RefreshCw, Search, ChevronDown, ChevronUp } from 'lucide-react'

const LEVELS = ['error', 'warn', 'info']
const LEVEL_CONFIG: Record<string, { label: string; bg: string; text: string; Icon: any }> = {
  error: { label: 'Erro',  bg: 'bg-red-100',   text: 'text-red-700',   Icon: AlertCircle },
  warn:  { label: 'Aviso', bg: 'bg-amber-100', text: 'text-amber-700', Icon: AlertTriangle },
  info:  { label: 'Info',  bg: 'bg-blue-100',  text: 'text-blue-700',  Icon: Info },
}

function LevelBadge({ level }: { level: string }) {
  const cfg = LEVEL_CONFIG[level] ?? LEVEL_CONFIG.info
  const { Icon } = cfg
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  )
}

function LogRow({ log }: { log: any }) {
  const [expanded, setExpanded] = useState(false)
  const hasExtra = log.details || log.stack_trace
  return (
    <>
      <tr
        className={`hover:bg-gray-50 ${hasExtra ? 'cursor-pointer' : ''}`}
        onClick={() => hasExtra && setExpanded(e => !e)}
      >
        <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">{formatDateTime(log.created_at)}</td>
        <td className="px-4 py-2"><LevelBadge level={log.level} /></td>
        <td className="px-4 py-2">
          {log.tag && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600">{log.tag}</span>
          )}
        </td>
        <td className="px-4 py-2 text-sm text-gray-800 max-w-md truncate">{log.message}</td>
        <td className="px-4 py-2 text-xs text-gray-400">{log.app_version ?? '-'}</td>
        <td className="px-4 py-2 text-xs text-gray-400">{log.platform ?? '-'}</td>
        <td className="px-4 py-2 text-xs text-gray-400">{log.user_id ?? '-'}</td>
        <td className="px-4 py-2 text-xs text-gray-300">
          {hasExtra && (expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
        </td>
      </tr>
      {expanded && hasExtra && (
        <tr className="bg-gray-50">
          <td colSpan={8} className="px-6 py-3 space-y-3">
            {log.details && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Details</p>
                <pre className="text-xs text-gray-700 bg-white rounded border px-3 py-2 overflow-auto max-h-40 whitespace-pre-wrap">{log.details}</pre>
              </div>
            )}
            {log.stack_trace && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Stack Trace</p>
                <pre className="text-xs text-red-600 bg-white rounded border px-3 py-2 overflow-auto max-h-60 whitespace-pre-wrap">{log.stack_trace}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export default function AppLogsPage() {
  const qc = useQueryClient()
  const [level, setLevel] = useState('')
  const [tag, setTag] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['app-logs', page, level, tag, search],
    queryFn: () => appLogsApi.list({
      page,
      page_size: 50,
      level: level || undefined,
      tag: tag || undefined,
      search: search || undefined,
    }),
    refetchInterval: 60000,
  })

  const clearMut = useMutation({
    mutationFn: (days: number) => appLogsApi.clear(days),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-logs'] }); setPage(1) },
  })

  const logs: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.total ?? 0
  const totalPages = Math.ceil(total / 50) || 1
  const errorCount = logs.filter(l => l.level === 'error').length
  const warnCount = logs.filter(l => l.level === 'warn').length

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-500" /> Telemetria — Logs da App
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Erros e eventos capturados da app Android em tempo real. Clica numa linha para ver detalhes/stack trace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()}
            className="flex items-center gap-1 rounded-lg border px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw className="h-4 w-4" /> Atualizar
          </button>
          <button
            onClick={() => { if (confirm('Eliminar logs com mais de 30 dias?')) clearMut.mutate(30) }}
            className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Limpar &gt;30d
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="mb-4 flex gap-3 flex-wrap">
        {[
          { label: 'Total (pagina)', value: logs.length, bg: 'bg-gray-100', text: 'text-gray-700' },
          { label: 'Erros', value: errorCount, bg: 'bg-red-100', text: 'text-red-700' },
          { label: 'Avisos', value: warnCount, bg: 'bg-amber-100', text: 'text-amber-700' },
          { label: 'Total (todos)', value: total, bg: 'bg-blue-100', text: 'text-blue-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg px-4 py-2 ${s.bg}`}>
            <p className={`text-xs font-medium ${s.text}`}>{s.label}</p>
            <p className={`text-xl font-bold ${s.text}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input className="input pl-8 text-sm w-56" placeholder="Pesquisar mensagem..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
        </div>
        <select className="input text-sm" value={level}
          onChange={e => { setLevel(e.target.value); setPage(1) }}>
          <option value="">Todos os niveis</option>
          {LEVELS.map(l => <option key={l} value={l}>{LEVEL_CONFIG[l].label}</option>)}
        </select>
        <input className="input text-sm w-44 font-mono" placeholder="tag (ex: profile_save)"
          value={tag} onChange={e => { setTag(e.target.value); setPage(1) }} />
      </div>

      {/* Table */}
      <div className="card overflow-x-auto p-0">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <AlertCircle className="mx-auto mb-3 h-10 w-10 opacity-30" />
            <p>Sem logs. Quando a app Android enviar telemetria aparece aqui.</p>
            <p className="mt-1 text-xs text-gray-300">Os logs chegam em batches de 10 ou a cada 30 segundos.</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Data/Hora', 'Nivel', 'Tag', 'Mensagem', 'Versao App', 'Plataforma', 'User ID', ''].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {logs.map((log: any) => <LogRow key={log.id} log={log} />)}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-sm text-gray-500">
                  Pagina {page} de {totalPages} ({total} total)
                </span>
                <div className="flex gap-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    className="btn btn-secondary text-sm disabled:opacity-40">Anterior</button>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                    className="btn btn-secondary text-sm disabled:opacity-40">Seguinte</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
